import React, { useState } from 'react';
import {
  FiCode as Code, FiCopy as Copy, FiCheck as Check,
  FiAlertTriangle as AlertTriangle, FiRefreshCw as RefreshCw, FiClock as Clock, FiShield as Shield, FiTerminal as Terminal, FiBook as Book,
  FiFileText as FileText, FiSettings as SettingsIcon, FiActivity as Activity, FiZap as Zap, FiTarget as Target,
  FiKey as Key
} from 'react-icons/fi';
interface CodeBlockProps {
  code: string;
  id: string;
  language: string;
}

const SDKContent: React.FC = () => {
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [activeIntegration, setActiveIntegration] = useState('openai');

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSnippet(id);
      setTimeout(() => setCopiedSnippet(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Code examples
  const installCode = `# Install from PyPI (recommended)
pip install aivilo-sdk

# Or install from GitHub
pip install git+https://github.com/aiviloorg/aivilo-sdk.git

# Or install specific version
pip install git+https://github.com/aiviloorg/aivilo-sdk.git@v0.2.0`;

  const quickStartCode = `from aivilo import AiviloClient

# Initialize the client with your API key
client = AiviloClient(
    api_key="ak_your_api_key_here",
    base_url="https://your-genaiops-api.example.com"
)

# Fetch a prompt from production
prompt = client.get_prompt(
    name="customer-support",
    environment="production"
)

# View template and variables
print(f"Template: {prompt.template}")
print(f"Variables: {prompt.get_variables()}")

# Compile the prompt with your variables
message = prompt.compile(
    customer_name="John",
    issue="billing question"
)

print(message)`;

  const configurationCode = `from aivilo import AiviloClient

# Method 1: Explicit configuration
client = AiviloClient(
    # Required: Your API key from the API Keys page
    api_key="ak_xxxxx",

    # Required: Your GenAIOps API URL
    base_url="https://your-genaiops-api.example.com",

    # Optional: Request timeout in seconds (default: 30)
    timeout=30.0,

    # Optional: Enable local file caching (default: True)
    cache_enabled=True,

    # Optional: Cache time-to-live in seconds (default: 300 = 5 min)
    cache_ttl=300,

    # Optional: Custom cache directory (default: ~/.aivilo/cache)
    cache_dir="/path/to/cache"
)

# Method 2: Initialize from environment variables
# Requires AIVILO_API_KEY and AIVILO_BASE_URL env vars
client = AiviloClient.from_env()

# Method 3: Initialize from config file (~/.aivilo/config.yaml)
client = AiviloClient.from_config()`;

  const configFileCode = `# ~/.aivilo/config.yaml
api_key: ak_your_api_key_here
base_url: https://your-genaiops-api.example.com
timeout: 30.0
cache_enabled: true
cache_ttl: 300
cache_dir: ~/.aivilo/cache`;

  const envVarsCode = `import os
from aivilo import AiviloClient

# Store credentials in environment variables
# export AIVILO_API_KEY="ak_xxxxx"
# export AIVILO_BASE_URL="https://your-api.example.com"

client = AiviloClient(
    api_key=os.environ["AIVILO_API_KEY"],
    base_url=os.environ["AIVILO_BASE_URL"]
)

# Dynamic environment based on deployment
env = os.environ.get("DEPLOYMENT_ENV", "development")
prompt = client.get_prompt("my-prompt", environment=env)`;

  const openaiIntegrationCode = `from openai import OpenAI
from aivilo import AiviloClient

# Initialize clients
aivilo = AiviloClient(api_key="ak_xxx", base_url="...")
openai = OpenAI()

# Fetch and compile prompt
prompt = aivilo.get_prompt("chat-assistant", environment="production")
system_message = prompt.compile(
    context="User is asking about their subscription",
    tone="friendly and helpful"
)

# Use with OpenAI
response = openai.chat.completions.create(
    model=prompt.config.model if prompt.config else "gpt-4",
    messages=[
        {"role": "system", "content": system_message},
        {"role": "user", "content": "What's my billing date?"}
    ],
    temperature=prompt.config.temperature if prompt.config else 0.7,
)

print(response.choices[0].message.content)`;

  const anthropicIntegrationCode = `import anthropic
from aivilo import AiviloClient

# Initialize clients
aivilo = AiviloClient(api_key="ak_xxx", base_url="...")
claude = anthropic.Anthropic()

# Fetch and compile prompt
prompt = aivilo.get_prompt("analysis-prompt", environment="production")
message = prompt.compile(
    document="Q3 financial report...",
    focus_areas="revenue growth, cost reduction"
)

# Use with Claude
response = claude.messages.create(
    model="claude-3-5-sonnet-20241022",
    max_tokens=1024,
    messages=[{"role": "user", "content": message}]
)

print(response.content[0].text)`;

  const langchainIntegrationCode = `from langchain.prompts import PromptTemplate
from langchain_openai import ChatOpenAI
from aivilo import AiviloClient

# Fetch prompt from Aivilo
aivilo = AiviloClient(api_key="ak_xxx", base_url="...")
aivilo_prompt = aivilo.get_prompt("qa-prompt", environment="production")

# Convert to LangChain PromptTemplate
# Aivilo uses {{var}} syntax, LangChain uses {var}
langchain_template = aivilo_prompt.template.replace("{{", "{").replace("}}", "}")
langchain_prompt = PromptTemplate.from_template(langchain_template)

# Use in a chain
llm = ChatOpenAI(model="gpt-4")
chain = langchain_prompt | llm

result = chain.invoke({
    "question": "What is machine learning?",
    "context": "Technical documentation"
})`;

  const datasetsCode = `from aivilo import AiviloClient

client = AiviloClient.from_env()

# Fetch a dataset from your environment
dataset = client.get_dataset(
    name="customer-support-examples",
    environment="production"
)

# Access test cases
for test_case in dataset:
    print(f"Input: {test_case.input_data}")
    print(f"Expected: {test_case.expected_output}")
    print("---")

# Dataset info
print(f"Dataset: {dataset.name} v{dataset.version_number}")
print(f"Total test cases: {len(dataset)}")`;

  const testRunnerCode = `from aivilo import AiviloClient
from aivilo.runner import TestRunner

client = AiviloClient.from_env()

# Fetch prompt and dataset
prompt = client.get_prompt("code-reviewer", environment="production")
dataset = client.get_dataset("code-review-tests", environment="production")

# Initialize TestRunner
runner = TestRunner(client, pass_threshold=0.7)

# Define your agent function
def my_agent(input_data: str) -> dict:
    # Your LLM call here (OpenAI, Anthropic, Bedrock, etc.)
    response = call_your_llm(input_data)
    return {
        "output": response,
        "input_tokens": 100,  # Optional: token tracking
        "output_tokens": 50,
        "cost": 0.001
    }

# Run evaluation
summary = runner.run_dataset(
    dataset=dataset,
    agent_fn=my_agent,
    prompt=prompt,
    scoring_method="contains",  # or "exact", "similarity", "llm_judge"
    model_id="gpt-4"  # For tracking in UI
)

# Print results
print(summary.summary())
# Output:
# Total Tests:   10
# Passed:        8 (80.0%)
# Failed:        2
# Avg Score:     0.85

# Upload results to GenAIOps
response = runner.upload_results(summary, evaluation_name="my-eval-run")
print(f"Evaluation ID: {response['evaluation_run_id']}")`;

  const llmJudgeCode = `from aivilo.runner import TestRunner

# Create an LLM judge function for semantic evaluation
def create_llm_judge(openai_client):
    def judge(actual_output: str, expected_criteria: str) -> float:
        """Score how well actual output meets expected criteria (0.0-1.0)"""
        response = openai_client.chat.completions.create(
            model="gpt-4o-mini",  # Fast, cheap model for judging
            messages=[{
                "role": "user",
                "content": f"""Score how well this output meets the criteria.

Expected criteria: {expected_criteria}

Actual output:
{actual_output[:3000]}

Respond with JSON only: {{"score": <0.0-1.0>, "reasoning": "<brief>"}}"""
            }],
            response_format={"type": "json_object"}
        )
        result = json.loads(response.choices[0].message.content)
        return float(result.get("score", 0.0))
    return judge

# Use LLM judge with TestRunner
from openai import OpenAI
openai = OpenAI()
judge_fn = create_llm_judge(openai)

runner = TestRunner(
    client,
    pass_threshold=0.7,
    judge_fn=judge_fn  # Pass your judge function
)

summary = runner.run_dataset(
    dataset=dataset,
    agent_fn=my_agent,
    scoring_method="llm_judge"  # Use LLM-based semantic scoring
)

# Now semantic criteria like "should identify SQL injection"
# will be properly evaluated even if exact text doesn't match`;

  const tracerCode = `from aivilo import AiviloClient, AiviloTracer

# Initialize tracer for OpenTelemetry integration
tracer = AiviloTracer("my-agent-service")

# Create spans for your agent operations
with tracer.span("agent_run", attributes={
    "session.id": "user-123",
    "agent.name": "code-reviewer"
}) as span:
    # Your agent logic here
    prompt = client.get_prompt("code-reviewer")
    response = call_llm(prompt.compile(code=user_code))

    # Add result attributes
    span.set_attribute("tokens.total", 150)
    span.set_attribute("response.length", len(response))

# Traces are automatically sent to GenAIOps OTLP endpoint
# View them in Observability > Traces

# Configure OTLP endpoint (usually automatic)
import os
os.environ["OTEL_EXPORTER_OTLP_ENDPOINT"] = "https://your-api/api/v1/agentops/otlp"`;

  const scoringMethodsCode = `# Available scoring methods for TestRunner

# 1. "contains" (default) - Check if expected is substring of actual
#    Score: 1.0 if expected in actual, else 0.0
runner.run_dataset(dataset, agent_fn, scoring_method="contains")

# 2. "exact" - Exact string match (case-insensitive)
#    Score: 1.0 if strings match exactly, else 0.0
runner.run_dataset(dataset, agent_fn, scoring_method="exact")

# 3. "similarity" - Levenshtein distance similarity
#    Score: 0.0-1.0 based on edit distance
runner.run_dataset(dataset, agent_fn, scoring_method="similarity")

# 4. "llm_judge" - LLM-based semantic evaluation
#    Score: 0.0-1.0 from your judge_fn callback
#    Best for: code reviews, summaries, creative tasks
runner = TestRunner(client, judge_fn=my_judge_fn)
runner.run_dataset(dataset, agent_fn, scoring_method="llm_judge")`;

  const bedrockIntegrationCode = `import boto3
from aivilo import AiviloClient

# Initialize clients
aivilo = AiviloClient.from_env()
bedrock = boto3.client("bedrock-runtime", region_name="us-east-1")

# Fetch prompt from Aivilo
prompt = aivilo.get_prompt("analysis-prompt", environment="production")
message = prompt.compile(
    document="Quarterly report data...",
    analysis_type="financial"
)

# Use with AWS Bedrock (Claude)
response = bedrock.invoke_model(
    modelId="anthropic.claude-3-sonnet-20240229-v1:0",
    body={
        "anthropic_version": "bedrock-2023-05-31",
        "max_tokens": 1024,
        "messages": [{"role": "user", "content": message}]
    }
)

print(response["body"].read().decode())`;

  const errorHandlingCode = `from aivilo import (
    AiviloClient,
    AiviloError,
    AuthenticationError,
    PromptNotFoundError,
    NetworkError,
)

client = AiviloClient(api_key="ak_xxx", base_url="...")

try:
    prompt = client.get_prompt("my-prompt", environment="production")
    message = prompt.compile(name="John")

except AuthenticationError:
    # Invalid or expired API key (401)
    print("Check your API key")

except PromptNotFoundError as e:
    # Prompt doesn't exist (404)
    print(f"Prompt '{e.prompt_name}' not found in {e.environment}")

except NetworkError:
    # Network issues - SDK will try to use cached version
    print("Network error, using cached prompt if available")

except AiviloError as e:
    # Other API errors
    print(f"Error: {e.message} (status: {e.status_code})")`;

  const CodeBlock: React.FC<CodeBlockProps> = ({ code, id, language }) => (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
        <span className="text-xs text-gray-400">{language}</span>
        <button
          onClick={() => copyToClipboard(code, id)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          {copiedSnippet === id ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto">
        <code className="text-sm text-gray-300 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header with version and links */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Code className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Aivilo SDK</h2>
              <span className="px-2 py-1 text-xs font-medium bg-white/20 rounded-full">
                v0.2.0
              </span>
            </div>
            <p className="text-blue-100">
              Python SDK for fetching and using prompts from Aivilo Lens with built-in caching and offline support.
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href="https://github.com/aiviloorg/aivilo-sdk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Terminal className="w-4 h-4" />
              GitHub
            </a>
          </div>
        </div>
      </div>

      {/* Installation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
            <Terminal className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Installation
          </h2>
        </div>
        <CodeBlock code={installCode} id="install" language="bash" />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Requirements File</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Add to your <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">requirements.txt</code>:
            </p>
            <code className="text-xs bg-gray-900 text-green-400 px-2 py-1 rounded block">
              git+https://github.com/aiviloorg/aivilo-sdk.git@v0.2.0
            </code>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Requirements</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Python 3.9+ required. Dependencies: <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">httpx</code>, <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">pydantic</code>, <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">pyyaml</code>
            </p>
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
            <Book className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Quick Start
          </h2>
        </div>
        <CodeBlock code={quickStartCode} id="quickstart" language="python" />
      </div>

      {/* Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
            <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configuration
          </h2>
        </div>
        <CodeBlock code={configurationCode} id="config" language="python" />
        <div className="mt-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Config File</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Create a config file at <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">~/.aivilo/config.yaml</code> for persistent configuration:
          </p>
          <CodeBlock code={configFileCode} id="configfile" language="yaml" />
        </div>
        <div className="mt-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Environment Variables</h3>
          <CodeBlock code={envVarsCode} id="envvars" language="python" />
        </div>
      </div>

      {/* LLM Integrations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-lg">
            <Code className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            LLM Integrations
          </h2>
        </div>

        {/* Integration tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {(['openai', 'anthropic', 'bedrock', 'langchain'] as const).map((id) => {
            const integrationLabels: Record<string, string> = {
              openai: 'OpenAI',
              anthropic: 'Anthropic',
              bedrock: 'AWS Bedrock',
              langchain: 'LangChain',
            };
            const label = integrationLabels[id] ?? 'LangChain';
            return (
            <button
              key={id}
              onClick={() => setActiveIntegration(id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeIntegration === id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
            );
          })}
        </div>

        {activeIntegration === 'openai' && (
          <CodeBlock code={openaiIntegrationCode} id="openai" language="python" />
        )}
        {activeIntegration === 'anthropic' && (
          <CodeBlock code={anthropicIntegrationCode} id="anthropic" language="python" />
        )}
        {activeIntegration === 'bedrock' && (
          <CodeBlock code={bedrockIntegrationCode} id="bedrock" language="python" />
        )}
        {activeIntegration === 'langchain' && (
          <CodeBlock code={langchainIntegrationCode} id="langchain" language="python" />
        )}
      </div>

      {/* Datasets */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
            <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Datasets
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Fetch datasets from your GenAIOps registry for evaluations, testing, or fine-tuning workflows.
        </p>
        <CodeBlock code={datasetsCode} id="datasets" language="python" />
      </div>

      {/* TestRunner - Evaluations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg">
            <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            TestRunner - Evaluations
          </h2>
          <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full">
            New
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Run automated evaluations against your datasets and upload results to GenAIOps for tracking and comparison.
        </p>
        <CodeBlock code={testRunnerCode} id="testrunner" language="python" />

        {/* Scoring Methods */}
        <div className="mt-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Scoring Methods</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Choose the scoring method that best fits your evaluation criteria:
          </p>
          <CodeBlock code={scoringMethodsCode} id="scoring" language="python" />
        </div>
      </div>

      {/* LLM-as-Judge */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-lg">
            <Zap className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            LLM-as-Judge Scoring
          </h2>
          <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-full">
            New
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Use an LLM to semantically evaluate if agent outputs meet expected criteria. Perfect for code reviews, summaries, and creative tasks where exact text matching doesn't work.
        </p>
        <CodeBlock code={llmJudgeCode} id="llmjudge" language="python" />
        <div className="mt-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
          <h4 className="font-medium text-violet-900 dark:text-violet-200 mb-2">When to use LLM Judge</h4>
          <ul className="text-sm text-violet-800 dark:text-violet-300 space-y-1 list-disc list-inside">
            <li>Code review evaluations (e.g., "should identify SQL injection")</li>
            <li>Summary quality assessment</li>
            <li>Creative writing evaluation</li>
            <li>Any task where semantic meaning matters more than exact text</li>
          </ul>
        </div>
      </div>

      {/* OpenTelemetry Tracer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-cyan-100 dark:bg-cyan-900/50 rounded-lg">
            <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            OpenTelemetry Tracing
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Instrument your agent with OpenTelemetry traces for observability. View traces in the GenAIOps Traces page.
        </p>
        <CodeBlock code={tracerCode} id="tracer" language="python" />
      </div>

      {/* Error Handling */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Error Handling
          </h2>
        </div>
        <CodeBlock code={errorHandlingCode} id="errors" language="python" />
      </div>

      {/* Environments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Environments</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">development</h3>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              For testing and experimentation. Default environment.
            </p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">staging</h3>
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              For pre-production validation and QA testing.
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 dark:text-green-200 mb-2">production</h3>
            <p className="text-sm text-green-800 dark:text-green-300">
              For live production systems. Use promote to deploy here.
            </p>
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
            <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Best Practices
          </h2>
        </div>
        <div className="space-y-4">
          {[
            { num: 1, title: 'Secure Your API Keys', desc: 'Never commit API keys to version control. Use environment variables or secret management systems like AWS Secrets Manager.' },
            { num: 2, title: 'Use Caching', desc: 'Keep caching enabled (default) to reduce API calls and improve performance. The SDK handles cache invalidation automatically based on TTL.' },
            { num: 3, title: 'Match Environments', desc: 'Use the same environment in your SDK calls as your deployment context (dev → development, prod → production).' },
            { num: 4, title: 'Handle Errors Gracefully', desc: 'Always catch exceptions and implement fallbacks. The SDK will use cached prompts on network errors automatically.' },
            { num: 5, title: 'Use Context Manager', desc: 'Use "with AiviloClient(...) as client:" to ensure HTTP connections are properly closed.' }
          ].map((item) => (
            <div key={item.num} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {item.num}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SDK Features */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          SDK Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { icon: Target, title: 'TestRunner', desc: 'Run evaluations and upload results to GenAIOps automatically', isNew: true },
            { icon: Zap, title: 'LLM-as-Judge', desc: 'Semantic evaluation using LLM for code reviews, summaries', isNew: true },
            { icon: Activity, title: 'OpenTelemetry', desc: 'Built-in tracing with AiviloTracer for observability', isNew: true },
            { icon: Shield, title: 'Automatic Caching', desc: 'Built-in file caching with configurable TTL for offline support' },
            { icon: Clock, title: 'Environment Support', desc: 'Fetch prompts from development, staging, or production' },
            { icon: Key, title: 'Secure Authentication', desc: 'API key-based authentication with rotation support' },
            { icon: Code, title: 'Multiple Providers', desc: 'Works with OpenAI, Anthropic, AWS Bedrock, LangChain' },
            { icon: Terminal, title: 'Template Variables', desc: 'Extract and compile {{variables}} in your prompts' },
            { icon: RefreshCw, title: 'Offline Fallback', desc: 'Automatically uses cached prompts when network is unavailable' },
            { icon: FileText, title: 'Dataset Support', desc: 'Fetch datasets for evaluations, testing, or fine-tuning' },
            { icon: SettingsIcon, title: 'Flexible Config', desc: 'Initialize via code, env vars, or YAML config file' }
          ].map((feature) => (
            <div key={feature.title} className={`flex items-start gap-3 p-4 rounded-lg ${feature.isNew ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
              <feature.icon className={`w-5 h-5 mt-0.5 ${feature.isNew ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`} />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  {feature.title}
                  {feature.isNew && (
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded">
                      New
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SDKContent;
